import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { PleasewaitPopup } from "../../../components/PleasewaitPopup";
import { baseURL } from "../../../config/api";
import useAuth from "../../../hooks/useAuth";
import CustomDateTimePicker from "../../../components/CustomFormatDateTimePicker";
import moment from "moment";

export const EditSeason = () => {
    const navigate = useNavigate();
    const idNumber: any = useLocation();

    const { auth }: any = useAuth();

    const [name, setName] = useState<string>("");
    const [code, setCode] = useState<string>("");
    const [seasonType, setSeasonType] = useState<string>("recurring");

    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");

    const [startMonth, setStartMonth] = useState<string>("");
    const [startDay, setStartDay] = useState<string>("");
    const [endMonth, setEndMonth] = useState<string>("");
    const [endDay, setEndDay] = useState<string>("");

    const [priority, setPriority] = useState<string>("0");
    const [enabled, setEnabled] = useState<string>("Yes");
    const [description, setDescription] = useState<string>("");

    const [pleaseWaitPopup, setPleaseWaitPopup] =
        useState<boolean>(false);

    const [seasonData, setSeasonData] = useState<any>(null);

    const [dateTouched, setDateTouched] = useState<
        "startDate" | "endDate" | ""
    >("");

    const isDateInvalid =
        startDate &&
        endDate &&
        moment(endDate).isSameOrBefore(moment(startDate));

    useEffect(() => {
        setPleaseWaitPopup(true);

        axios
            .get(`${baseURL}seasons/${idNumber?.state}`)
            .then((response) => {
                const season = response?.data?.data;

                setSeasonData(season);

                setName(season?.name ?? "");
                setCode(season?.code ?? "");
                setSeasonType(season?.season_type ?? "recurring");

                setStartDate(
                    season?.start_date
                        ? formatDateTimeLocal(season.start_date)
                        : ""
                );

                setEndDate(
                    season?.end_date
                        ? formatDateTimeLocal(season.end_date)
                        : ""
                );

                setStartMonth(
                    season?.start_month != null
                        ? String(season.start_month)
                        : ""
                );

                setStartDay(
                    season?.start_day != null
                        ? String(season.start_day)
                        : ""
                );

                setEndMonth(
                    season?.end_month != null
                        ? String(season.end_month)
                        : ""
                );

                setEndDay(
                    season?.end_day != null
                        ? String(season.end_day)
                        : ""
                );

                setPriority(
                    season?.priority != null
                        ? String(season.priority)
                        : "0"
                );

                setEnabled(
                    season?.is_active
                        ? "Yes"
                        : "No"
                );

                setDescription(
                    season?.description ?? ""
                );

                setPleaseWaitPopup(false);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);
                console.error("Error fetching season:", error);
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idNumber]);

    const formatDateTimeLocal = (date: string) => {
        if (!date) return "";

        const d = new Date(date);

        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");

        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const handleSubmit = async (
        e: React.FormEvent<HTMLFormElement>
    ) => {
        e.preventDefault();

        setPleaseWaitPopup(true);

        const payload = {
            ...seasonData,

            name: name.trim(),
            code: code.trim(),
            season_type: seasonType,

            start_date: startDate
                ? new Date(startDate).toISOString()
                : null,

            end_date: endDate
                ? new Date(endDate).toISOString()
                : null,

            start_month:
                seasonType === "recurring"
                    ? Number(startMonth)
                    : null,

            start_day:
                seasonType === "recurring"
                    ? Number(startDay)
                    : null,

            end_month:
                seasonType === "recurring"
                    ? Number(endMonth)
                    : null,

            end_day:
                seasonType === "recurring"
                    ? Number(endDay)
                    : null,

            priority: Number(priority),

            is_active: enabled === "Yes",

            description: description.trim(),

            updated_at: new Date().toISOString(),
            updated_by: auth?.givenname
        };

        await axios
            .put(
                `${baseURL}seasons/${idNumber?.state}`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json"
                    },
                    withCredentials: false
                }
            )
            .then((response) => {
                setPleaseWaitPopup(false);
                navigate(-1);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);

                console.error(
                    "Error updating season:",
                    error
                );
            });
    };


    return (
        <>
            <div className="px-[24px] py-[20px] h-[calc(100vh-142px)] overflow-auto">

                <div className="w-full bg-white p-4 rounded-md">

                    <h2 className="text-lg font-semibold">
                        Edit Season
                    </h2>

                    <div className="mt-2 p-2">

                        <form onSubmit={handleSubmit}>

                            <div className="w-full">

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-2.5">

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Season Name{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <input
                                            type="text"
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={name}
                                            placeholder="e.g. Winter Holiday Season"
                                            onChange={(e) =>
                                                setName(e.target.value)
                                            }
                                            required
                                        />
                                    </div>


                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Code{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <select
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={code}
                                            onChange={(e) => setCode(e.target.value)}
                                            required
                                        >
                                            <option value="">
                                                Select Code
                                            </option>

                                            <option value="SUMMER">
                                                SUMMER
                                            </option>

                                            <option value="WINTER">
                                                WINTER
                                            </option>

                                            <option value="HOLIDAY">
                                                HOLIDAY
                                            </option>

                                            <option value="PEAK">
                                                PEAK
                                            </option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Season Type{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <select
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={seasonType}
                                            onChange={(e) =>
                                                setSeasonType(e.target.value)
                                            }
                                            required
                                        >
                                            <option value="recurring">
                                                Recurring
                                            </option>

                                            <option value="fixed">
                                                Fixed
                                            </option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Priority{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <input
                                            type="number"
                                            min="0"
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={priority}
                                            onChange={(e) => {
                                                const value =
                                                    e.target.value;

                                                if (
                                                    value === "" ||
                                                    Number(value) >= 0
                                                ) {
                                                    setPriority(value);
                                                }
                                            }}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-2.5">

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">
                                            Start Date
                                        </label>

                                        <CustomDateTimePicker
                                            value={startDate}
                                            maxDate="9999-12-31T23:59"
                                            onChange={(value) => {
                                                setDateTouched("startDate");
                                                setStartDate(value);
                                            }}
                                        />

                                        {isDateInvalid && dateTouched === "startDate" && (
                                            <p className="text-xs text-red-500 mt-1">
                                                Start Date must be less than End Date.
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">
                                            End Date
                                        </label>

                                        <CustomDateTimePicker
                                            value={endDate}
                                            minDate={startDate || undefined}
                                            maxDate="9999-12-31T23:59"
                                            onChange={(value) => {
                                                setDateTouched("endDate");
                                                setEndDate(value);
                                            }}
                                        />

                                        {isDateInvalid && dateTouched === "endDate" && (
                                            <p className="text-xs text-red-500 mt-1">
                                                End Date must be greater than Start Date.
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {seasonType === "recurring" && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-2.5">

                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                                Start Month{" "}
                                                <span className="text-red-500">
                                                    *
                                                </span>
                                            </label>

                                            <select
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                value={startMonth}
                                                onChange={(e) =>
                                                    setStartMonth(
                                                        e.target.value
                                                    )
                                                }
                                                required
                                            >
                                                <option value="">
                                                    Select Month
                                                </option>

                                                <option value="1">
                                                    January
                                                </option>
                                                <option value="2">
                                                    February
                                                </option>
                                                <option value="3">
                                                    March
                                                </option>
                                                <option value="4">
                                                    April
                                                </option>
                                                <option value="5">
                                                    May
                                                </option>
                                                <option value="6">
                                                    June
                                                </option>
                                                <option value="7">
                                                    July
                                                </option>
                                                <option value="8">
                                                    August
                                                </option>
                                                <option value="9">
                                                    September
                                                </option>
                                                <option value="10">
                                                    October
                                                </option>
                                                <option value="11">
                                                    November
                                                </option>
                                                <option value="12">
                                                    December
                                                </option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                                Start Day{" "}
                                                <span className="text-red-500">
                                                    *
                                                </span>
                                            </label>

                                            <input
                                                type="number"
                                                min="1"
                                                max="31"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                value={startDay}
                                                placeholder="1 - 31"
                                                onChange={(e) => {
                                                    const value =
                                                        e.target.value;

                                                    if (
                                                        value === "" ||
                                                        (
                                                            Number(value) >= 1 &&
                                                            Number(value) <= 31
                                                        )
                                                    ) {
                                                        setStartDay(value);
                                                    }
                                                }}
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                                End Month{" "}
                                                <span className="text-red-500">
                                                    *
                                                </span>
                                            </label>

                                            <select
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                value={endMonth}
                                                onChange={(e) =>
                                                    setEndMonth(
                                                        e.target.value
                                                    )
                                                }
                                                required
                                            >
                                                <option value="">
                                                    Select Month
                                                </option>

                                                <option value="1">
                                                    January
                                                </option>
                                                <option value="2">
                                                    February
                                                </option>
                                                <option value="3">
                                                    March
                                                </option>
                                                <option value="4">
                                                    April
                                                </option>
                                                <option value="5">
                                                    May
                                                </option>
                                                <option value="6">
                                                    June
                                                </option>
                                                <option value="7">
                                                    July
                                                </option>
                                                <option value="8">
                                                    August
                                                </option>
                                                <option value="9">
                                                    September
                                                </option>
                                                <option value="10">
                                                    October
                                                </option>
                                                <option value="11">
                                                    November
                                                </option>
                                                <option value="12">
                                                    December
                                                </option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                                End Day{" "}
                                                <span className="text-red-500">
                                                    *
                                                </span>
                                            </label>

                                            <input
                                                type="number"
                                                min="1"
                                                max="31"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                value={endDay}
                                                placeholder="1 - 31"
                                                onChange={(e) => {
                                                    const value =
                                                        e.target.value;

                                                    if (
                                                        value === "" ||
                                                        (
                                                            Number(value) >= 1 &&
                                                            Number(value) <= 31
                                                        )
                                                    ) {
                                                        setEndDay(value);
                                                    }
                                                }}
                                                required
                                            />
                                        </div>

                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-2.5">

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Active
                                        </label>

                                        <select
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={enabled}
                                            onChange={(e) =>
                                                setEnabled(e.target.value)
                                            }
                                        >
                                            <option value="Yes">
                                                Yes
                                            </option>

                                            <option value="No">
                                                No
                                            </option>
                                        </select>
                                    </div>


                                    <div className="md:col-span-2">

                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Description
                                        </label>

                                        <textarea
                                            rows={2}
                                            value={description}
                                            onChange={(e) =>
                                                setDescription(
                                                    e.target.value
                                                )
                                            }
                                            placeholder="Enter season description"
                                            className="w-full p-4 border border-gray-300 rounded-md bg-white resize-none outline-none focus:border-primary"
                                        />

                                    </div>

                                </div>

                                <div className="flex flex-col sm:flex-row gap-4 mt-6">

                                    <button
                                        type="submit"
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-primary text-white cursor-pointer"
                                    >
                                        Save
                                    </button>


                                    <button
                                        type="button"
                                        onClick={() => navigate(-1)}
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-white text-primary cursor-pointer border border-primary"
                                    >
                                        Cancel
                                    </button>

                                </div>

                            </div>

                        </form>

                    </div>

                </div>

            </div>

            <PleasewaitPopup
                showPopup={pleaseWaitPopup}
            />
        </>
    );
};